/**
 * RIG mode (left column, above the outliner): where the rest skeleton sits, and the joint the
 * user is currently moving. The joint *list* is the hierarchy panel; the two closing actions
 * (generate weights, confirm rig) live in the left footer — `rigFooterPanel.ts` — so they cannot
 * be scrolled away by a long outliner.
 */

import { CANONICAL_PARENT } from '../../shared';
import type { Vec3Tuple } from '../../shared';
import { mirrorPlacement } from '../../rig/skeletonBuilder';
import type { EditorContext } from '../context';
import type { Controllers } from '../controllers/types';
import { button, el, icon, iconButton, numberInput, switchField } from './dom';
import {
  createPanel,
  field,
  note,
  readonlyField,
  section,
  type PanelHandle,
} from './panel';

const AXES: ReadonlyArray<{ index: 0 | 1 | 2; label: string }> = [
  { index: 0, label: 'X' },
  { index: 1, label: 'Y' },
  { index: 2, label: 'Z' },
];

export function mountRigPanel(
  container: HTMLElement,
  ctx: EditorContext,
  controllers: Controllers,
): PanelHandle {
  const { store } = ctx;

  function mirror(from: 'left' | 'right'): void {
    const placement = store.getState().rig.placement;
    if (!placement) return;
    store.actions.setPlacement(mirrorPlacement(placement, from));
  }

  function render(root: HTMLElement): void {
    const state = store.getState();
    const rig = state.rig;

    root.appendChild(
      section(
        'Skeleton placement',
        [
          // One row, short labels: three separate rows of long labels wrapped at 280px and pushed
          // the outliner (the selection tool) off the screen (audit B).
          el('div', { class: 'buttons' }, [
            button([icon('expand'), 'Estimate'], () => controllers.rig.estimatePlacement(), {
              'data-action': 'estimate',
            }),
            button([icon('mirror'), 'L→R'], () => mirror('left'), {
              disabled: !rig.placement,
              title: 'Mirror the left side onto the right',
            }),
            button([icon('mirror'), 'R→L'], () => mirror('right'), {
              disabled: !rig.placement,
              title: 'Mirror the right side onto the left',
            }),
            iconButton(
              'reset',
              'Reset the placement to the bounds estimate',
              () => controllers.rig.estimatePlacement(),
              { 'data-action': 'reset' },
            ),
          ]),
          switchField('Symmetry while dragging', rig.symmetry, (checked) =>
            store.actions.setSymmetry(checked),
          ),
          rig.placement ? null : note('No placement yet — estimate it from the model bounds.'),
        ],
        { icon: 'bone' },
      ),
    );

    const joint = rig.selectedJoint;
    const position = joint && rig.placement ? rig.placement.jointWorldPositions[joint] : null;
    root.appendChild(
      section(
        'Selected joint',
        position && joint
          ? [
              ...AXES.map(({ index, label }) =>
                field(
                  `Position ${label}`,
                  numberInput(
                    // Displayed to millimetre precision: the slot is 22px tall and the full
                    // double never fit. Editing still commits whatever the user types.
                    Number(position[index].toFixed(4)),
                    (value) => {
                      const next: Vec3Tuple = [position[0], position[1], position[2]];
                      next[index] = value;
                      store.actions.moveJoint(joint, next);
                    },
                    { step: '0.005', 'data-axis': label },
                  ),
                ),
              ),
              readonlyField('Parent', CANONICAL_PARENT[joint] ?? '—'),
            ]
          : [note('Select a joint in the hierarchy to edit its position.')],
        { meta: joint ?? undefined, icon: 'target' },
      ),
    );
  }

  /*
   * Only the slices this panel actually renders. Subscribing to the whole `rig` slice (plus
   * 'skin-changed') rebuilt the joint fields on every live re-solve — i.e. once per animation
   * frame while a correction slider is dragged — throwing away half-typed numberInput text.
   * The skin slices moved out with the buttons that read them (`rigFooterPanel.ts`).
   */
  return createPanel(container, ctx, render, {
    className: 'rig',
    slices: [(s) => s.rig.placement, (s) => s.rig.selectedJoint, (s) => s.rig.symmetry],
  });
}
